import { useState } from "react";
import { Pencil, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export function EditCompanyDialog({ company, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const openDialog = () => {
    setForm({
      name: company?.name || "",
      org_number: company?.org_number || "",
      address: company?.address || "",
      postal_code: company?.postal_code || "",
      city: company?.city || "",
      industry: company?.industry || "",
      share_capital: company?.share_capital ?? "",
      innkalling_days: company?.innkalling_days ?? 14,
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!String(form.name).trim()) {
      toast.error("Selskapsnavn kan ikke være tomt");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/companies/${company.id}`, {
        ...form,
        share_capital: form.share_capital === "" ? 0 : Number(form.share_capital),
        innkalling_days: form.innkalling_days === "" ? 14 : Number(form.innkalling_days),
      });
      toast.success("Firmainformasjon oppdatert");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button variant="outline" size="sm" className="h-8 gap-1.5" data-testid="edit-company-btn" onClick={openDialog}>
        <Pencil className="h-3.5 w-3.5" /> Rediger
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="edit-company-dialog">
          <DialogHeader>
            <DialogTitle>Rediger firmainformasjon</DialogTitle>
            <DialogDescription>Oppdater selskapets grunndata. Antall aksjer styres av aksjonærene.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3 py-1">
            <div className="col-span-2">
              <Label className="text-xs">Selskapsnavn</Label>
              <Input data-testid="edit-company-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Org.nr</Label>
              <Input data-testid="edit-company-org" className="mt-1.5" value={form.org_number} onChange={(e) => set("org_number", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Bransje</Label>
              <Input data-testid="edit-company-industry" className="mt-1.5" value={form.industry} onChange={(e) => set("industry", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Adresse</Label>
              <Input data-testid="edit-company-address" className="mt-1.5" value={form.address} onChange={(e) => set("address", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Postnr</Label>
              <Input data-testid="edit-company-postal" className="mt-1.5" value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} />
            </div>
            <div>
              <Label className="text-xs">Poststed</Label>
              <Input data-testid="edit-company-city" className="mt-1.5" value={form.city} onChange={(e) => set("city", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Aksjekapital (kr)</Label>
              <Input data-testid="edit-company-capital" type="number" inputMode="numeric" className="mt-1.5" value={form.share_capital} onChange={(e) => set("share_capital", e.target.value)} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs">Innkallingsfrist generalforsamling (dager før)</Label>
              <Input data-testid="edit-company-innkalling-days" type="number" inputMode="numeric" min={1} max={60} className="mt-1.5" value={form.innkalling_days} onChange={(e) => set("innkalling_days", e.target.value)} />
              <p className="mt-1 text-[11px] text-muted-foreground">Formell innkalling sendes automatisk så mange dager før generalforsamlingen (f.eks. 14 eller 21).</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Avbryt</Button>
            <Button className="gap-2" data-testid="edit-company-save" onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Lagre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
